import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AuthProvider } from "./contexts/AuthContext";
import { LocaleProvider } from "./i18n/LocaleContext";
import { SoundProvider } from "./contexts/SoundContext";
import { Toaster } from "sonner";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

import Landing from "./pages/Landing";
import VehicleList from "./pages/VehicleList";
import DropDetail from "./pages/DropDetail";
import Cinema from "./pages/Cinema";
import Journal from "./pages/Journal";
import JournalArticle from "./pages/JournalArticle";
import Privacy from "./pages/Privacy";
import ChiSiamo from "./pages/ChiSiamo";
import Servizi from "./pages/Servizi";

import AdminLogin from "./pages/admin/AdminLogin";
import { AdminRoute } from "./components/ProtectedRoute";

// Admin bundle lazy-loaded
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminDrops = lazy(() => import("./pages/admin/AdminDrops"));
const AdminDropForm = lazy(() => import("./pages/admin/AdminDropForm"));
const AdminMembers = lazy(() => import("./pages/admin/AdminMembers"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminVideos = lazy(() => import("./pages/admin/AdminVideos"));
const AdminJournal = lazy(() => import("./pages/admin/AdminJournal"));
const AdminJournalEdit = lazy(() => import("./pages/admin/AdminJournalEdit"));
const AdminHomePanels = lazy(() => import("./pages/admin/AdminHomePanels"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminInstagram = lazy(() => import("./pages/admin/AdminInstagram"));

function AnimatedRoutes() {
  const location = useLocation();

  React.useEffect(() => {
    const isAdmin = location.pathname.startsWith("/vault-9821");
    document.documentElement.dataset.theme = isAdmin ? "dark" : "light";
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <Routes location={location}>
          <Route path="/" element={<Landing />} />
          <Route path="/auto" element={<VehicleList type="auto" />} />
          <Route path="/moto" element={<VehicleList type="moto" />} />
          <Route path="/veicolo/:slug" element={<DropDetail />} />
          {/* legacy detail routes → same page */}
          <Route path="/drops/:slug" element={<DropDetail />} />
          <Route path="/cinema" element={<Cinema />} />
          <Route path="/chi-siamo" element={<ChiSiamo />} />
          <Route path="/servizi" element={<Servizi />} />
          <Route path="/marketplace" element={<Navigate to="/auto" replace />} />
          <Route path="/team" element={<Navigate to="/chi-siamo" replace />} />
          <Route path="/giornale" element={<Journal />} />
          <Route path="/giornale/:slug" element={<JournalArticle />} />
          <Route path="/privacy" element={<Privacy />} />

          {/* legacy redirects to keep old links alive */}
          <Route path="/upcoming-drops" element={<Navigate to="/auto" replace />} />
          <Route path="/drops" element={<Navigate to="/auto" replace />} />
          <Route path="/servizi-noleggio" element={<Navigate to="/" replace />} />
          <Route path="/progetti-speciali" element={<Navigate to="/" replace />} />
          <Route path="/contatti" element={<Navigate to="/" replace />} />
          <Route path="/contact" element={<Navigate to="/" replace />} />

          {/* Admin */}
          <Route path="/vault-9821" element={<AdminLogin />} />
          <Route path="/admin" element={<Navigate to="/vault-9821" replace />} />
          <Route
            element={
              <AdminRoute>
                <Suspense
                  fallback={
                    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#86868B", fontSize: 13, letterSpacing: "0.3em", textTransform: "uppercase" }}>
                      Caricamento pannello…
                    </div>
                  }
                >
                  <AdminLayout />
                </Suspense>
              </AdminRoute>
            }
          >
            <Route path="/vault-9821/dashboard" element={<AdminDashboard />} />
            <Route path="/vault-9821/home-panels" element={<AdminHomePanels />} />
            <Route path="/vault-9821/services" element={<AdminServices />} />
            <Route path="/vault-9821/instagram" element={<AdminInstagram />} />
            <Route path="/vault-9821/drops" element={<AdminDrops />} />
            <Route path="/vault-9821/drops/new" element={<AdminDropForm />} />
            <Route path="/vault-9821/drops/:id/edit" element={<AdminDropForm />} />
            <Route path="/vault-9821/journal" element={<AdminJournal />} />
            <Route path="/vault-9821/journal/new" element={<AdminJournalEdit />} />
            <Route path="/vault-9821/journal/:id/edit" element={<AdminJournalEdit />} />
            <Route path="/vault-9821/videos" element={<AdminVideos />} />
            <Route path="/vault-9821/members" element={<AdminMembers />} />
            <Route path="/vault-9821/newsletter" element={<AdminNewsletter />} />
          </Route>
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LocaleProvider>
          <SoundProvider>
            <AuthProvider>
              <Navbar />
              <Toaster
                theme="dark"
                position="top-center"
                toastOptions={{
                  style: {
                    background: "#111111",
                    color: "#FFFFFF",
                    border: "1px solid rgba(255,255,255,0.08)",
                  },
                }}
              />
              <AnimatedRoutes />
              <Footer />
            </AuthProvider>
          </SoundProvider>
        </LocaleProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
